import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Order } from "../../../../../types/order";
import { RootState } from "../../../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { calculateDuration } from "../../../../../util/date";
import { Reservation } from "../../../../../types/bookingCreation";
import { atStation } from "../../../../../util/stationTime";
import { useSelectedLocationTimeZone } from "../../../../../util/locationTimeZone";
import {
  couponRedemptionLoading,
  reservationRemoved,
  setAssetGroup,
  setSelectedGroup,
} from "../../../../../redux/slice/bookingCreation/bookingCreationSlice";
import DeleteDialog from "../../../common/components/ConfirmationStep/DeleteDialog";
import { useState } from "react";
import { useRemoveReservation } from "../../../../../redux/flows/removeReservationFlow";
import { useApplyCoupon } from "../../../../../redux/flows/applyCouponFlow";
import LoaderOverlay from "../../../../layout/components/LoaderOverlay";
import {
  calculateDisplayPrice,
  calculateTotalPrice,
  isFreeOfCharge,
  consolidateCoupons,
  findCoupons,
} from "../../../../../util/payments";
import { formatCurrency, getEquipmentName } from "../../../../../util";
import TrashIcon from "../../../../../components/icons/TrashIcon";
import { cn } from "../../../../../util/cn";
import { i18n } from "../../../../../i18n/i18n";
import CustomButton from "../../../common/util/CustomButton";
import { useTaxStatement } from "../../../../../util/taxStatement";

interface BookingTicketProps {
  order?: Order;
  location?: string;
  reservations: Reservation[];
  amount?: number;
}

const BookingTicket = (props: BookingTicketProps) => {
  const { location, reservations, amount } = props;
  const [couponCode, setCouponCode] = useState<string>("");
  const { t } = useTranslation();
  const taxStatement = useTaxStatement();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [selectedReservationId, setSelectedReservationId] = useState<string>();
  const [selectedEquipment, setSelectedEquipment] = useState<string>();
  const dispatch = useDispatch();
  const removeReservation = useRemoveReservation();
  const tenantConfig = useSelector((state: RootState) => state.tenantConfig.config);
  // The booked location's clock (#3219).
  const timeZone = useSelectedLocationTimeZone();
  const selectedLocation = useSelector(
    (state: RootState) => state.bookingCreation.selectedLocation
  );
  const equipments = useSelector((state: RootState) =>
    selectedLocation ? state.bookingCreation.groupsByLocation[selectedLocation.id] : undefined
  );
  const isCouponReedemptionLoading = useSelector(
    (state: RootState) => state.bookingCreation.loading.couponRedemption
  );

  const appliedCouponCode = useSelector(
    (state: RootState) => state.bookingCreation.appliedCouponCode
  );
  const [showLoading, setShowLoading] = useState(false);
  const applyCoupon = useApplyCoupon();

  // Basic coupon validation - alphanumeric with optional dashes/underscores
  const isValidCoupon = (code: string) => {
    return /^[a-zA-Z0-9-_ ]+$/.test(code) && code.trim().length > 0;
  };

  const isApplyButtonDisabled = !isValidCoupon(couponCode);

  const applyCouponOnReservationDeletion = async (reservationIds: string[]) => {
    dispatch(couponRedemptionLoading({ loading: true }));

    if (!appliedCouponCode) return;

    await applyCoupon(reservationIds, appliedCouponCode);
  };

  const findEquipmentName = (groupId: string) => {
    if (!equipments) return "";
    const targetGroup = equipments.find((group: any) => group.id === groupId);
    if (targetGroup) {
      return getEquipmentName(targetGroup.name, targetGroup?.langNames, "title");
    }

    return "";
  };

  const handleDeleteReservation = async (reservationId: string) => {
    setShowLoading(true);

    dispatch(reservationRemoved({ reservationId }));
    await removeReservation(reservationId);

    const remainingReservationIds = reservations
      .filter((reservation) => reservation.reservationId !== reservationId)
      .map((reservation) => reservation.reservationId!);

    if (remainingReservationIds.length) {
      await applyCouponOnReservationDeletion(remainingReservationIds);
    }

    if (equipments && equipments.length > 0) {
      dispatch(setSelectedGroup(equipments[equipments?.length - 1]));
      dispatch(setAssetGroup({ id: equipments[equipments?.length - 1].id }));
    }

    setShowLoading(false);
    dispatch(couponRedemptionLoading({ loading: false }));
  };

  const handleDelete = (reservationId: string, equipmentName: string) => {
    setDeleteDialogOpen(true);
    setSelectedReservationId(reservationId);
    setSelectedEquipment(equipmentName);
  };

  const onApplyCoupon = async () => {
    if (isApplyButtonDisabled) return;

    dispatch(couponRedemptionLoading({ loading: true }));

    const reservationIds = reservations.map((reservation: any) => {
      return reservation.reservationId;
    });

    await applyCoupon(reservationIds, couponCode);

    setCouponCode("");
  };

  const coupons = findCoupons(reservations);
  const totalPrice = calculateTotalPrice(reservations);
  const displayPrice = calculateDisplayPrice(totalPrice, coupons);

  // A promo code cannot reduce a free booking. ADR-0008 amendment 2026-09-16.
  const free = isFreeOfCharge(totalPrice, reservations.length);
  const showCouponButton = tenantConfig.allowCoupons && coupons.length === 0 && !free;

  const consolidatedCoupons = consolidateCoupons(coupons);
  const textBoldClass = "text-foreground/80 font-bold leading-5 tracking-[0.02em] break-words";

  return (
    <div className="bg-background shadow-md flex flex-col py-8 px-5 border border-foreground/10 relative mx-5 rounded-md text-foreground">
      <div className="absolute -right-px top-[29%] h-[14px] w-[7px] rounded-l-full border border-r-0 border-foreground/10 bg-white" />
      <div className="absolute -left-px top-[29%] h-[14px] w-[7px] rounded-r-full border border-l-0 border-foreground/10 bg-white" />
      <div>
        <p>{t("orders.ticket_overview.location")}</p>
        <p className={textBoldClass}>{location}</p>
      </div>
      <div className="mt-2">
        <p>{t("orders.ticket_overview.booked_items")}</p>
        {reservations.map((reservation, _) => {
          // Extract start and end dates from reservationItems
          const reservationStartDate = atStation(reservation.reservationItems[0].fromDate, timeZone);
          const reservationEndDate = atStation(reservation.reservationItems[0].toDate, timeZone);
          const equipmentName = findEquipmentName(reservation.reservationItems[0].groupId);
          const price = reservation.payment.totalPrice;
          const quantity = reservation.reservationItems.length;
          const duration = calculateDuration(
            reservationStartDate.toDate(),
            reservationEndDate.toDate()
          );
          return (
            <div
              key={reservation.id}
              className="w-full flex flex-col mb-1.5 border-b border-foreground/45"
            >
              <div
                className={cn(textBoldClass, "flex space-between w-full flex-row justify-between")}
              >
                <strong>
                  {quantity}x {equipmentName} -{" "}
                  {duration.days > 0 &&
                    `${duration.days} ${
                      duration.days === 1
                        ? t("general.duration_day_singular")
                        : t("general.duration_day_plural")
                    }`}
                  {duration.hours > 0 && ` ${duration.hours}${t("general.duration_hour_abbr")}`}{" "}
                  {duration.minutes > 0 &&
                    ` ${duration.minutes}${t("general.duration_minute_abbr")}`}{" "}
                </strong>
                <strong>{formatCurrency(price, i18n.language)}</strong>
              </div>
              <div>
                <p>
                  {reservationStartDate.format("L")}{" "}
                  {reservationStartDate.format("LT")}
                </p>
              </div>
              <div className="flex flex-row justify-between">
                <p>
                  {reservationEndDate.format("L")} {reservationEndDate.format("LT")}
                </p>
                <Button
                  size="small"
                  onClick={() => handleDelete(reservation.reservationId, equipmentName)}
                  className="!size-auto p-1 !min-w-0 !-mr-1 rounded-full"
                >
                  <TrashIcon className="text-red-500" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div>
        {consolidatedCoupons.map((coupon) => (
          <div className="flex flex-row justify-between" key={coupon.code}>
            <p>
              {t("book_location.confirmation.coupon_item")} ({coupon.code})
            </p>
            <p className={textBoldClass}>
              {!showLoading
                ? formatCurrency(
                    (coupon.amount > totalPrice ? totalPrice : coupon.amount) * -1,
                    i18n.language
                  )
                : "..."}
            </p>
          </div>
        ))}

        <div className="flex flex-row justify-between">
          <p>{t("book_location.confirmation.total_items")}</p>
          <p className={textBoldClass}>
            {!showLoading ? formatCurrency(displayPrice, i18n.language) : "..."}
          </p>
        </div>
        {showCouponButton && (
          <div className="flex flex-row justify-between items-center gap-4 my-2">
            <input
              type="text"
              aria-label={t("general.enter_coupon_code")}
              placeholder={t("general.enter_coupon_code")}
              onChange={(e) => setCouponCode(e.target.value)}
              className=" px-3 h-10 py-2 w-full border border-foreground/10 bg-background rounded-lg outline-none focus:border-primary"
            />
            <CustomButton
              onClick={onApplyCoupon}
              loading={isCouponReedemptionLoading}
              disabled={isApplyButtonDisabled}
              spinnerColorClass="border-onPrimary border-t-primary"
              className="mt-2 bg-primary text-onPrimary"
            >
              {t("orders.ticket_overview.button")}
            </CustomButton>
          </div>
        )}
        {/* No price, nothing for a tax statement to be about. */}
        {!free && (
          <p className="text-sm">{taxStatement.sentence}</p>
        )}
      </div>
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onContinue={() =>
          selectedReservationId ? handleDeleteReservation(selectedReservationId) : null
        }
        content={t("book_location.confirmation.delete_reservation_description", {
          amount: amount,
          equipment: selectedEquipment,
        })}
        amount={amount}
        equipmentName={selectedEquipment}
      />

      <LoaderOverlay open={showLoading} />
    </div>
  );
};
export default BookingTicket;
